import { useState, useEffect } from 'react';
import type { CardFormData, SubscriptionPlan } from '../../types/signup';
import { ProgressIndicator } from './ProgressIndicator';

interface PaymentFormProps {
  plans: SubscriptionPlan[];
  plansLoading: boolean;
  onSubmit: (cardData: CardFormData, planId: number) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  /** Present when the server requires 3DS authentication */
  authUrl: string | null;
  /** Called once the 3DS iframe posts back the authenticationId */
  onAuthComplete: (authenticationId: string) => void;
  /** Called when the user dismisses the 3DS overlay after a failure/error/expiry */
  onAuthError: () => void;
  /** Fires on every keystroke so the parent can drive the live preview */
  onCardDataChange?: (data: CardFormData, planId: number | null) => void;
}

const formatCardInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  const groups = [];
  for (let i = 0; i < digits.length; i += 4) {
    groups.push(digits.slice(i, i + 4));
  }
  return groups.join(' ');
};

export const PaymentForm = ({
  plans,
  plansLoading,
  onSubmit,
  onBack,
  loading,
  error,
  authUrl,
  onAuthComplete,
  onAuthError,
  onCardDataChange,
}: PaymentFormProps) => {
  const [auth3dsError, setAuth3dsError] = useState(false);

  // Reset error state whenever a new 3DS URL is received
  useEffect(() => {
    if (authUrl) setAuth3dsError(false);
  }, [authUrl]);

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(
    plans.length > 0 ? plans[0].planId : null
  );
  const [cardData, setCardData] = useState<CardFormData>({
    card_holder: '',
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
    customer_phone: '',
  });

  // Update selectedPlanId once plans load (if not yet set)
  const effectivePlanId = selectedPlanId ?? (plans[0]?.planId ?? null);

  const handleCardChange = (field: keyof CardFormData, raw: string) => {
    setCardData((prev) => {
      let next: CardFormData;
      if (field === 'card_number') {
        next = { ...prev, card_number: formatCardInput(raw) };
      } else if (field === 'expiry_month') {
        next = { ...prev, expiry_month: raw.replace(/\D/g, '').slice(0, 2) };
      } else if (field === 'expiry_year') {
        next = { ...prev, expiry_year: raw.replace(/\D/g, '').slice(0, 4) };
      } else if (field === 'cvv') {
        next = { ...prev, cvv: raw.replace(/\D/g, '').slice(0, 4) };
      } else {
        next = { ...prev, [field]: raw };
      }
      onCardDataChange?.(next, effectivePlanId);
      return next;
    });
  };

  const isFormValid =
    effectivePlanId !== null &&
    cardData.card_holder.trim().length > 2 &&
    cardData.card_number.replace(/\s/g, '').length === 16 &&
    cardData.expiry_month.length >= 1 &&
    cardData.expiry_year.length === 4 &&
    cardData.cvv.length >= 3 &&
    cardData.customer_phone.trim().length >= 7;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || effectivePlanId === null) return;
    onSubmit(cardData, effectivePlanId);
  };

  const inputClass =
    'w-full bg-white border-2 border-[#101822]/5 rounded-2xl px-5 py-4 text-base text-[#101822] placeholder-[#101822]/20 focus:border-primary focus:ring-4 focus:ring-primary/5 focus:bg-white transition-all duration-300 outline-none shadow-sm';
  const labelClass = 'block text-xs font-bold text-[#101822]/50 uppercase tracking-widest mb-2';

  return (
    <>
      {/* 3DS auth iframe overlay */}
      {authUrl && (
        <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center gap-4 p-8">
          <p className="text-sm font-bold text-[#101822]/60 uppercase tracking-widest">
            Verificación de seguridad
          </p>
          <p className="text-center text-[#101822]/50 text-sm max-w-xs">
            Tu banco requiere una verificación adicional. Completa el proceso en el siguiente
            formulario.
          </p>
          <iframe
            src={authUrl}
            className="w-full max-w-sm h-96 rounded-2xl border border-[#101822]/10 shadow-xl"
            title="Autenticación 3DS"
            onLoad={() => {
              const handler = (event: MessageEvent) => {
                if (event.origin !== import.meta.env.VITE_N1CO_3DS_ORIGIN) return;

                if (!event.data) return;
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                // Success: both camelCase (our server proxy) and PascalCase (n1co direct)
                const authId = data.authenticationId ?? data.AuthenticationId;
                const status = data.Status ?? data.status;
                const msgType = data.MessageType ?? data.messageType;

                if (authId && (msgType === 'authentication.complete' || status === 'SUCCESS')) {
                  window.removeEventListener('message', handler);
                  onAuthComplete(authId);
                  return;
                }

                // Failure: FAILED, ERROR, or EXPIRED statuses
                const isFailure =
                  msgType === 'authentication.failed' ||
                  status === 'FAILED' ||
                  status === 'ERROR' ||
                  status === 'EXPIRED';

                if (isFailure) {
                  window.removeEventListener('message', handler);
                  setAuth3dsError(true);
                }
              };
              window.addEventListener('message', handler);
            }}
          />
          {auth3dsError && (
            <div className="flex items-center gap-2 text-red-500 bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
              <span className="material-icons-round text-base">error_outline</span>
              <span className="text-sm font-semibold">
                La autenticación 3DS falló o expiró. Regresa para intentar con otra tarjeta.
              </span>
            </div>
          )}

          {loading && !auth3dsError && (
            <div className="flex items-center gap-2 text-primary">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              <span className="text-sm font-semibold">Completando suscripción…</span>
            </div>
          )}

          <button
            onClick={onAuthError}
            disabled={!auth3dsError}
            className="flex items-center gap-2 text-[#101822]/60 hover:text-primary transition-colors text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-[#101822]/60"
          >
            <span className="material-icons-round text-xl">arrow_back</span>
            Regresar a detalles de la tarjeta
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto no-scrollbar p-8 md:p-16 flex flex-col justify-center">
        <ProgressIndicator currentStep={3} />

        {/* Back */}
        <button
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-2 text-[#101822]/60 hover:text-primary transition-colors mb-6 disabled:opacity-50"
        >
          <span className="material-icons-round text-xl">arrow_back</span>
          <span className="text-sm font-semibold">Volver a los datos de usuario</span>
        </button>

        <div className="space-y-8">
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#101822] tracking-tight leading-tight text-balance">
              Activa tu suscripción
            </h1>
            <p className="text-[#101822]/50 text-sm leading-relaxed">
              Elige un plan e ingresa los datos de tu tarjeta para completar el registro.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Plan Selector */}
            <div className="space-y-3">
              <label className={labelClass}>Plan</label>
              {plansLoading ? (
                <div className="flex items-center gap-3 p-4 rounded-2xl border-2 border-[#101822]/5 bg-[#f5f8ff]">
                  <svg className="animate-spin h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <span className="text-sm text-[#101822]/40">Cargando planes…</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {plans.map((plan) => (
                    <button
                      key={plan.planId}
                      type="button"
                      onClick={() => {
                        setSelectedPlanId(plan.planId);
                        onCardDataChange?.(cardData, plan.planId);
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                        effectivePlanId === plan.planId
                          ? 'border-primary bg-[#f0f6ff]'
                          : 'border-[#101822]/5 bg-white hover:border-primary/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                            effectivePlanId === plan.planId
                              ? 'border-primary bg-primary'
                              : 'border-[#101822]/20'
                          }`}
                        >
                          {effectivePlanId === plan.planId && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-[#101822] text-sm">{plan.name}</p>
                          <p className="text-xs text-[#101822]/40 truncate max-w-[160px]">
                            {plan.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="font-extrabold text-primary">
                          {plan.currencyCode}{' '}
                          {plan.paymentLink.amount.toLocaleString('es', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <p className="text-[10px] text-[#101822]/30">
                          /
                          {plan.billingCycleType === 'monthly'
                            ? 'mes'
                            : plan.billingCycleType === 'annual'
                            ? 'año'
                            : plan.billingCycleType}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cardholder name */}
            <div>
              <label className={labelClass} htmlFor="card_holder">
                Nombre del titular
              </label>
              <input
                id="card_holder"
                type="text"
                autoComplete="cc-name"
                placeholder="Como aparece en la tarjeta"
                value={cardData.card_holder}
                onChange={(e) => handleCardChange('card_holder', e.target.value.toUpperCase())}
                className={inputClass}
              />
            </div>

            {/* Card number */}
            <div>
              <label className={labelClass} htmlFor="card_number">
                Número de tarjeta
              </label>
              <div className="relative">
                <input
                  id="card_number"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  placeholder="1234 5678 9012 3456"
                  value={cardData.card_number}
                  onChange={(e) => handleCardChange('card_number', e.target.value)}
                  className={`${inputClass} font-mono pr-14`}
                />
                <span className="material-icons-round absolute right-5 top-1/2 -translate-y-1/2 text-[#101822]/20 text-2xl">
                  credit_card
                </span>
              </div>
            </div>

            {/* Expiry + CVV */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass} htmlFor="expiry_month">
                  Mes
                </label>
                <input
                  id="expiry_month"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp-month"
                  placeholder="MM"
                  maxLength={2}
                  value={cardData.expiry_month}
                  onChange={(e) => handleCardChange('expiry_month', e.target.value)}
                  className={`${inputClass} text-center font-mono`}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="expiry_year">
                  Año
                </label>
                <input
                  id="expiry_year"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp-year"
                  placeholder="AAAA"
                  maxLength={4}
                  value={cardData.expiry_year}
                  onChange={(e) => handleCardChange('expiry_year', e.target.value)}
                  className={`${inputClass} text-center font-mono`}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="cvv">
                  CVV
                </label>
                <input
                  id="cvv"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder="•••"
                  maxLength={4}
                  value={cardData.cvv}
                  onChange={(e) => handleCardChange('cvv', e.target.value)}
                  className={`${inputClass} text-center font-mono tracking-widest`}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className={labelClass} htmlFor="customer_phone">
                Teléfono de contacto
              </label>
              <input
                id="customer_phone"
                type="tel"
                autoComplete="tel"
                placeholder="+57 300 000 0000"
                value={cardData.customer_phone}
                onChange={(e) => handleCardChange('customer_phone', e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
                <span className="material-icons-round text-base mt-0.5">error_outline</span>
                <span>{error}</span>
              </div>
            )}

            {/* Security note */}
            <div className="flex items-center gap-2 text-[#101822]/30 text-xs">
              <span className="material-icons-round text-base">lock</span>
              <span>Tus datos de tarjeta son cifrados y nunca almacenados en nuestros servidores.</span>
            </div>
          </form>
        </div>
      </div>

      {/* Submit */}
      <div className="p-8 md:p-12 border-t border-[#101822]/5 bg-white/60 backdrop-blur-xl">
        <button
          onClick={handleSubmit}
          disabled={loading || !isFormValid || plansLoading}
          className="w-full group relative overflow-hidden rounded-2xl !bg-primary !p-5 shadow-xl shadow-primary/20 transition-all duration-300 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          <div className="relative flex items-center justify-center gap-3">
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span className="font-bold text-white text-lg tracking-wide">Procesando pago…</span>
              </>
            ) : (
              <>
                <span className="font-bold text-white text-lg tracking-wide">Activar suscripción</span>
                <span className="material-icons-round text-white transition-transform group-hover:translate-x-1">
                  lock
                </span>
              </>
            )}
          </div>
        </button>
      </div>
    </>
  );
};
